// Приветственное письмо в окне после создания и в карточке приглашённого (035: T017, T018; FR-015,
// FR-017, FR-018; ONB-WEL-06, ONB-WEL-10; data-model.md, «Состояния письма для окна и карточки»;
// 001 contracts/ui.md, «Окно «Новый пользователь»»). Одна строка состояния на оба места: значок
// lucide и тон выноски по смыслу, смысл цвета повторяет текст (README «Цвет»); строка — в живой
// области aria-live="polite": программа чтения называет новое состояние, когда оно пришло.
// Состояние опрашивается GET /api/users/{id}/welcome раз в 2 с фоновым запросом (сеанс не
// продлевает, 001 FR-008б), пока письмо не в конечном состоянии, и не дольше 2 минут. Показывается
// письмо, номер которого назвал ответ (создание или «Отправить письмо заново»), или новее: номера
// писем — uuidv7 и растут со временем, поэтому сравниваются строкой. Прежнее письмо, пока новое
// не собрано, — «Готовим письмо…». Сбой запроса — выноска с причиной, кодом, номером операции
// и «Повторить» (README «Состояния и движение»).
// Правка ревью (T027, W1; сценарий 1.8, принцип XIV): у письма, которое ждёт или не ушло, — причина,
// следующий шаг и код: шаг по коду из каталога ошибок, под стоп-краном — где «Возобновить», как
// в карточке письма (mail/reason.ts). Ожидание остаётся янтарным, ошибка — красной.
// Правка ревью (T028, W2; FR-015, сценарий 1.6): ожидание — не конец. Письмо, которое ждёт
// подключения почты, предела Resend или «Возобновить», уходит само, поэтому опрос идёт дальше
// в пределах тех же 2 минут; конечные — только отправлено, доставлено, ошибка и тень.
import { shadowReasonLabels } from '@kp/contracts';
import type { MailWaitingReason, WelcomeLetter } from '@kp/contracts';
import { Button, Callout, useToast } from '@kp/ui';
import type { CalloutTone, LucideIcon } from '@kp/ui';
import { Clock, Hourglass, Mail, MailCheck, MailMinus, MailX, RotateCw, Send } from '@kp/ui/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api, apiKey, apiQuery, isApiError } from '../../api/client.ts';
import { useErrorToast } from '../../app/errors.ts';
import { formatDateTime } from '../../app/format.ts';
import { useBackgroundPoll } from '../../app/poll.ts';
import { can } from '../../shell/permissions.ts';
import { useMe } from '../../shell/session.ts';
import { ListLoadError, useQueryFailure } from '../load-error.tsx';
import { STOPPED_STEP, reasonStep } from '../mail/reason.ts';
import { Section } from './Section.tsx';
import { useRefreshUser } from './user-actions.tsx';

/** Опрос состояния письма, пока оно собирается и уходит. */
export const WELCOME_POLL_MS = 2000;
/** Дольше опрос не идёт: письмо, которое не ушло за 2 минуты, ждёт чего-то, что окно не ускорит. */
export const WELCOME_POLL_LIMIT_MS = 120_000;

export const WELCOME_SECTION_TITLE = 'Приветственное письмо';
export const SEND_AGAIN = 'Отправить письмо заново';
export const SENT_AGAIN = 'Письмо отправлено заново';
export const NO_LETTER = 'Письма не было';
export const WELCOME_STATE_FAILED = 'Не удалось узнать, ушло ли письмо';

/** Причина ожидания словами, если сервер не прислал своих (data-model.md: «ждёт с причиной»). */
const waitingWords = {
  sending_disabled: 'отправка писем отключена',
  connection_error: 'нужно исправить подключение почты',
  quota: 'исчерпан предел писем Resend',
  stopped: 'сообщения остановлены',
} as const satisfies Record<MailWaitingReason, string>;

const settledStates: ReadonlySet<WelcomeLetter['state']> = new Set([
  'sent',
  'sent_unknown',
  'delivered',
  'failed',
  'shadow',
]);

/**
 * Письмо в конечном состоянии: опрашивать дальше незачем. Ожидание с причиной — не конец: письмо
 * уйдёт само, когда почту подключат или сообщения возобновят (data-model.md, «Состояния письма»).
 */
export function welcomeSettled(letter: WelcomeLetter): boolean {
  return settledStates.has(letter.state);
}

/** Письмо — то, которое ждём, или новее (uuidv7 растут со временем). Не ждём — любое. */
export function isAwaitedLetter(letter: WelcomeLetter, letterId: string | null): boolean {
  return letterId === null || letter.id.toLowerCase() >= letterId.toLowerCase();
}

/**
 * Причина после двоеточия: строчная буква у русского слова («Все сообщения остановлены» →
 * «все сообщения остановлены»), аббревиатура и латиница («Resend не отвечает») — как есть;
 * точка в конце убирается — после строки может стоять «. Код ошибки …».
 */
function reasonText(text: string): string {
  const trimmed = text.trim().replace(/\.+$/, '');
  return /^[А-ЯЁ][а-яё]/.test(trimmed)
    ? trimmed.charAt(0).toLocaleLowerCase('ru-RU') + trimmed.slice(1)
    : trimmed;
}

export interface WelcomeView {
  tone: CalloutTone;
  icon: LucideIcon;
  text: string;
  /** Когда письмо ушло: «02.11.2026 15:42». */
  time?: string;
  /** Следующий шаг у ожидания и ошибки: из каталога ошибок по коду, под стоп-краном — «Возобновить». */
  step?: string;
  /** Код причины ожидания или ошибки: «MAIL-001». */
  code?: string;
}

/**
 * Шаг и код у письма, которое ждёт или не ушло: шаг по коду причины из каталога ошибок, под
 * стоп-краном — где «Возобновить» (шаг MAIL-022 из каталога говорит и о пробном письме — здесь
 * его нет). Точка в конце шага убирается — после него стоит «. Код ошибки …».
 */
function reasonParts(letter: WelcomeLetter): Pick<WelcomeView, 'step' | 'code'> {
  const code = letter.reason?.code;
  const step = letter.waiting === 'stopped' ? STOPPED_STEP : code ? reasonStep(code) : null;
  return {
    ...(step ? { step: step.trim().replace(/\.+$/, '') } : {}),
    ...(code ? { code } : {}),
  };
}

/**
 * Строка состояния письма по data-model.md. letter — показываемое письмо или null; preparing —
 * ответ назвал письмо, а его ещё нет: «Готовим письмо…»; email — почта пользователя, пока письма нет.
 */
export function welcomeView(
  letter: WelcomeLetter | null,
  { email, preparing, timeZone }: { email: string; preparing: boolean; timeZone: string },
): WelcomeView {
  if (!letter) {
    return preparing
      ? { tone: 'info', icon: Hourglass, text: `Готовим письмо на ${email}…` }
      : { tone: 'info', icon: Mail, text: NO_LETTER };
  }
  const to = letter.recipient || email;
  switch (letter.state) {
    case 'queued': {
      if (letter.waiting === null) {
        return { tone: 'info', icon: Send, text: `Письмо отправляется на ${to}` };
      }
      // Под стоп-краном — свои слова: MAIL-022 «Все сообщения остановлены» говорит то же.
      const reason = letter.reason?.text
        ? reasonText(letter.reason.text)
        : waitingWords[letter.waiting];
      return {
        tone: 'warn',
        icon: Clock,
        text: `Письмо ждёт отправки на ${to}: ${reason}`,
        ...reasonParts(letter),
      };
    }
    case 'sent':
    case 'sent_unknown':
    case 'delivered':
      return {
        tone: 'ok',
        icon: MailCheck,
        text: `Письмо отправлено на ${to}`,
        time: formatDateTime(letter.sentAt ?? letter.createdAt, timeZone),
      };
    case 'shadow': {
      const reasons = letter.shadow.map((code) => reasonText(shadowReasonLabels[code])).join(', ');
      return {
        tone: 'info',
        icon: MailMinus,
        text: reasons ? `Письмо не отправлено: ${reasons}` : 'Письмо не отправлено',
      };
    }
    case 'failed':
      return {
        tone: 'bad',
        icon: MailX,
        text: letter.reason
          ? `Письмо не ушло: ${reasonText(letter.reason.text)}`
          : 'Письмо не ушло',
        ...reasonParts(letter),
      };
  }
}

/**
 * Последнее письмо пользователя и опрос: раз в 2 с, пока письмо не в конечном состоянии (ожидание
 * опрашивается дальше), не дольше 2 минут от открытия или от нового запроса письма (letterId).
 */
function useWelcomeLetter(userId: string, letterId: string | null) {
  const query = useQuery(apiQuery('users.welcome', { params: { id: userId } }));
  const latest = query.data?.letter ?? null;
  const letter = latest && isAwaitedLetter(latest, letterId) ? latest : null;
  const preparing = letterId !== null && letter === null;

  // Срок опроса — у каждого запроса письма свой: новое «Отправить письмо заново» начинает отсчёт.
  const generation = `${userId}:${letterId ?? ''}`;
  const [expired, setExpired] = useState<string | null>(null);
  useEffect(() => {
    const timer = window.setTimeout(() => setExpired(generation), WELCOME_POLL_LIMIT_MS);
    return () => window.clearTimeout(timer);
  }, [generation]);

  const settled = letter ? welcomeSettled(letter) : !preparing && query.data !== undefined;
  // Сбой без данных — выноска с «Повторить»: опрос её не перебивает.
  const broken = query.isError && query.data === undefined;
  useBackgroundPoll(expired !== generation && !settled && !broken, query.refetch, WELCOME_POLL_MS);
  return { query, letter, preparing };
}

/**
 * Строка состояния приветственного письма: выноска со значком и тоном в живой области; сбой
 * запроса — выноска с причиной, кодом и «Повторить».
 */
export function WelcomeLetterStatus({
  userId,
  email,
  letterId,
  timeZone,
}: {
  userId: string;
  /** Почта пользователя: в строке, пока письма нет. */
  email: string;
  /** Номер письма из ответа создания или «Отправить письмо заново»; null — последнее письмо. */
  letterId: string | null;
  timeZone: string;
}) {
  const { query, letter, preparing } = useWelcomeLetter(userId, letterId);
  const { failure, retrying, retry } = useQueryFailure(query);

  // Сбой запроса — строки нет: вместо неё выноска с «Повторить» ниже.
  let line: ReactNode = null;
  if (!failure && query.data === undefined && !preparing) {
    line = <p className="t-muted">Загрузка…</p>;
  } else if (!failure) {
    const view = welcomeView(letter, { email, preparing, timeZone });
    line = (
      <Callout tone={view.tone} icon={view.icon} code={view.code}>
        {view.text}
        {view.time ? <span className="t-num"> · {view.time}</span> : null}
        {view.step ? `. ${view.step}` : null}
      </Callout>
    );
  }

  return (
    <div className="t-col">
      <div aria-live="polite" aria-atomic="true">
        {line}
      </div>
      {failure ? (
        <ListLoadError
          what={WELCOME_STATE_FAILED}
          view={failure}
          retrying={retrying}
          onRetry={retry}
        />
      ) : null}
    </div>
  );
}

/**
 * Раздел карточки приглашённого (T018; FR-017, FR-018; ONB-WEL-10): состояние последнего письма
 * и «Отправить письмо заново» — новое письмо с новой ссылкой (POST /api/users/{id}/welcome, право
 * users.invite), тост «Письмо отправлено заново» и опрос, как в окне. Ошибка — тост с причиной,
 * кодом и номером операции; пользователя перенесли в архив (REC-009) — без «Повторить»: тот же
 * запрос получит тот же отказ, карточка перечитывается.
 */
export function WelcomeLetterSection({
  userId,
  email,
  timeZone,
}: {
  userId: string;
  email: string;
  timeZone: string;
}) {
  const me = useMe();
  const toast = useToast();
  const showError = useErrorToast();
  const refresh = useRefreshUser();
  const queryClient = useQueryClient();
  const [letterId, setLetterId] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const sendAgain = async (): Promise<void> => {
    if (sending) return;
    setSending(true);
    let sent: { letterId: string };
    try {
      sent = await api('users.sendWelcome', { params: { id: userId } });
    } catch (error) {
      const archived = isApiError(error, 'REC-009');
      showError(error, archived ? undefined : () => void sendAgain());
      if (archived) void refresh();
      return;
    } finally {
      setSending(false);
    }
    setLetterId(sent.letterId);
    toast.ok(SENT_AGAIN);
    void queryClient.invalidateQueries({
      queryKey: apiKey('users.welcome', { params: { id: userId } }),
    });
    // Запись «Приветственное письмо отправлено заново» — в разделе «Журнал» карточки.
    await refresh();
  };

  return (
    <Section
      title={WELCOME_SECTION_TITLE}
      action={
        can(me.permissions, ['users.invite']) ? (
          <Button size="sm" icon={RotateCw} loading={sending} onClick={() => void sendAgain()}>
            {SEND_AGAIN}
          </Button>
        ) : undefined
      }
    >
      <WelcomeLetterStatus userId={userId} email={email} letterId={letterId} timeZone={timeZone} />
    </Section>
  );
}
